import { VectorFeatureControl } from "../controls/VectorFeatureControl";
import { getFGBLayer, validateFgbHeader } from "../layers/FGBLayer";
import { getHoverLayer } from "../layers/HoverLayer";
import {
  getLayerSlug,
  getLayersFromURL,
  registerLayerControl,
} from "../state/URLStateManager";

export function addFGBLayerAndControl({
  layerName,
  target,
  map,
  getInteractions,
  sortField,
  urls,
  style,
  featureInfoControl,
  transformFeature,
  minZoom = 17,
  settingConfigurationFn = function () {},
  hoverStyleFunctions,
  addFeatureHandlers = [],
}) {
  const slug = getLayerSlug(layerName);

  const control = new VectorFeatureControl({
    layer_name: layerName,
    target: target,
    sort_field: sortField,
    header_field: sortField,
  });

  // Restore layer visibility from URL. Re-applies when `enabled` becomes true,
  // because `enableIfAccessible` may reset the checkbox before confirming access.
  const shouldRestoreToggle = getLayersFromURL().includes(slug);
  if (shouldRestoreToggle) {
    control.setToggle(true);
  }
  registerLayerControl(slug, control);

  settingConfigurationFn(control, urls);
  const layer = getFGBLayer(
    control,
    urls,
    style,
    transformFeature,
    minZoom,
    addFeatureHandlers,
  );
  map.addControl(control);
  map.addLayer(layer);
  hoverStyleFunctions.forEach((hoverStyleFunction) => {
    const hoverLayer = getHoverLayer(hoverStyleFunction);
    map.addLayer(hoverLayer);
    featureInfoControl.addHoverLayerForLayer(hoverLayer, layer);
  });

  getInteractions(layer, control).forEach((interaction) =>
    map.addInteraction(interaction),
  );
  map.on("moveend", function () {
    control.set("zoom", map.getView().getZoom());
  });
}

export function getPreviousMonthsUrls(prefix, amountOfMonthsBack) {
  return Array.from({ length: amountOfMonthsBack }, (x, i) => {
    const today = new Date();
    today.setDate(Math.min(today.getDate(), 28));
    today.setMonth(today.getMonth() - i);
    return (prefix + "YYYY-MM.fgb")
      .replace("YYYY", today.getFullYear())
      .replace("MM", String(today.getMonth() + 1).padStart(2, "0"));
  });
}

export function enableIfAccessible(vectorFeatureControl, urls) {
  // Probe each URL by validating just its header. This doubles as the
  // accessibility check (a 401/403 rejects) and, crucially, avoids driving the
  // flatgeobuf reader into `calcTreeSize`'s synchronous infinite loop on a
  // malformed/empty file — which would freeze the tab here, at startup, before
  // any user interaction. See validateFgbHeader in FGBLayer.js.
  const promises = urls.map((url) => validateFgbHeader(url));

  Promise.any(promises)
    .then(() => {
      vectorFeatureControl.set("enabled", true);
    })
    .catch(() => {
      vectorFeatureControl.set("enabled", false);
    });
}

export function customizeFeatureId(featureIdFn) {
  return function (feature) {
    feature.setId(featureIdFn(feature));
    return feature;
  };
}

export function concatenateFeatureFields(fields) {
  return function (feature) {
    return fields.map((field) => feature.get(field)).join("-");
  };
}
